import type {
  CreatorFileProcessingResponse,
  ViewIncidentReportResponse,
  CreatorIncidentReportsPaginateReportsRequest
} from '@roadfootage/api-client'
import { UserProcessingState } from '@roadfootage/api-client'
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useApi } from '../../composables/api'
import { readApiErrorMessage } from '../../utils/api-error'

const REMOVE_FILE_ERROR = "We couldn't remove that file. Please try again."

export type RemoveFileResult = { ok: true } | { ok: false; message: string }

export const useCreatorStore = defineStore('creator', () => {
  const { creatorIncidentReportsApi } = useApi()

  const loading = ref<boolean>(false)
  const loadingDetails = ref<Record<string, boolean>>({})
  const incidentReportList = ref<ViewIncidentReportResponse[]>([])
  const hasMore = ref<boolean>(true)
  const removingFileId = ref<string | null>(null)

  // Per-report file progress from GET /creator/incident-reports/processing. A report id is present once fetched.
  const processingByReport = ref<Record<string, CreatorFileProcessingResponse[]>>({})

  const PROCESSING_POLL_MS = 5000
  const PROCESSING_MAX_IDS_PER_CALL = 20 // The API rejects more than this per request.
  let processingTimer: ReturnType<typeof setInterval> | null = null
  const PROCESSING_MAX_CONSECUTIVE_FAILURES = 3
  let processingPollInFlight = false
  // Cleared by stopProcessingPoll so a fetch that resolves after the view is gone cannot start polling.
  let processingPollAllowed = false
  let processingFailures = 0

  // Derived from item count so it can never drift out of sync across navigations.
  const PAGE_SIZE = 20
  const nextPage = computed(() => Math.floor(incidentReportList.value.length / PAGE_SIZE) + 1)

  function $reset() {
    stopProcessingPoll()
    processingByReport.value = {}
    loading.value = false
    loadingDetails.value = {}
    removingFileId.value = null
    incidentReportList.value = []
    hasMore.value = true
  }

  async function fetchCreatorIncidentReports() {
    if (loading.value) return
    loading.value = true
    processingPollAllowed = true

    const request: CreatorIncidentReportsPaginateReportsRequest = {
      pageSize: PAGE_SIZE,
      page: nextPage.value
    }

    try {
      const response =
        await creatorIncidentReportsApi.creatorIncidentReportsPaginateReports(request)

      if (response.items) {
        incidentReportList.value.push(...response.items)
        hasMore.value = response.items.length === PAGE_SIZE
        await refreshProcessing(response.items.map((i) => i.id!).filter(Boolean))
        startProcessingPoll()
      } else {
        hasMore.value = false
      }
    } catch (e) {
      console.error('Error fetching creator incident reports:', e)
    } finally {
      loading.value = false
    }
  }

  /**
   * Fetches progress for the given reports. Failures are logged and leave existing progress untouched.
   * Returns whether every batch succeeded, or that the session is unauthorised (401).
   */
  async function refreshProcessing(reportIds: string[]): Promise<'ok' | 'failed' | 'unauthorized'> {
    let outcome: 'ok' | 'failed' = 'ok'
    for (let i = 0; i < reportIds.length; i += PROCESSING_MAX_IDS_PER_CALL) {
      const batch = reportIds.slice(i, i + PROCESSING_MAX_IDS_PER_CALL)
      try {
        const files = await creatorIncidentReportsApi.creatorIncidentReportsGetProcessing({
          reportIds: batch
        })
        for (const id of batch) {
          processingByReport.value[id] = files.filter((f) => f.reportId === id)
        }
      } catch (e) {
        console.error('Error fetching processing progress:', e)
        if ((e as { response?: { status?: number } })?.response?.status === 401)
          return 'unauthorized'
        outcome = 'failed'
      }
    }
    return outcome
  }

  // Reports whose progress is unknown (a failed fetch) or has a file still processing.
  function reportIdsStillProcessing(): string[] {
    return incidentReportList.value
      .map((i) => i.id!)
      .filter((id) => {
        const files = processingByReport.value[id]
        return !files || files.some((f) => f.state === UserProcessingState.Processing)
      })
  }

  /** Polls every 5 s for reports that still have a file processing, and stops once none do. */
  function startProcessingPoll() {
    if (!processingPollAllowed || processingTimer || !reportIdsStillProcessing().length) return
    processingFailures = 0

    processingTimer = setInterval(async () => {
      if (processingPollInFlight) return
      const pending = reportIdsStillProcessing()
      if (!pending.length) {
        stopProcessingPoll()
        return
      }

      processingPollInFlight = true
      let outcome: Awaited<ReturnType<typeof refreshProcessing>>
      try {
        outcome = await refreshProcessing(pending)
      } finally {
        processingPollInFlight = false
      }

      // Every failed call raises an error toast, so give up rather than nag: on 401, or after repeated failures.
      processingFailures = outcome === 'failed' ? processingFailures + 1 : 0
      if (outcome === 'unauthorized' || processingFailures >= PROCESSING_MAX_CONSECUTIVE_FAILURES) {
        stopProcessingPoll()
        return
      }
      if (!reportIdsStillProcessing().length) stopProcessingPoll()
    }, PROCESSING_POLL_MS)
  }

  function stopProcessingPoll() {
    processingPollAllowed = false
    if (processingTimer) clearInterval(processingTimer)
    processingTimer = null
  }

  async function fetchIncidentReportDetails(id: string) {
    console.log('fetchIncidentReportDetails', { id })
    if (loadingDetails.value[id]) return

    loadingDetails.value[id] = true
    try {
      const request = { incidentReportId: id }
      const response =
        await creatorIncidentReportsApi.creatorIncidentReportsFetchReportByIncidentReportId(request)

      const index = incidentReportList.value.findIndex((i) => i.id === id)
      console.log('fetchIncidentReportDetails', { id, response, index })
      if (index !== -1) {
        Object.assign(incidentReportList.value[index], response)
      }
    } catch (e) {
      console.error(`Error fetching details for incident ${id}:`, e)
    } finally {
      loadingDetails.value[id] = false
    }
  }

  /**
   * Removes a file from a pending report. A refusal (409: reviewed, last file, still processing) carries the server's text.
   * Either way progress is refreshed so the list matches the server; on success the report details are reloaded too.
   */
  async function removeFile(reportId: string, fileId: string): Promise<RemoveFileResult> {
    if (removingFileId.value) return { ok: false, message: 'Already removing a file.' }
    removingFileId.value = fileId
    try {
      await creatorIncidentReportsApi.creatorIncidentReportsRemoveFileByIncidentReportIdAndFileId({
        incidentReportId: reportId,
        fileId
      })
    } catch (e) {
      console.error('Error removing file:', e)
      const message = await readApiErrorMessage(e, REMOVE_FILE_ERROR)
      removingFileId.value = null
      await refreshProcessing([reportId])
      return { ok: false, message }
    }

    removingFileId.value = null
    await refreshProcessing([reportId])
    await fetchIncidentReportDetails(reportId)
    return { ok: true }
  }

  return {
    $reset,
    removingFileId,
    removeFile,
    loading,
    loadingDetails,
    incidentReportList,
    processingByReport,
    startProcessingPoll,
    stopProcessingPoll,
    fetchCreatorIncidentReports,
    fetchIncidentReportDetails,
    hasMore
  }
})
